import { Loader } from "lucide-react";
import React, { useState } from "react";
import ChangeHistoryHeader from "./components/ChangeHistoryHeader";
import ChangeHistoryItemComponent from "./components/ChangeHistoryItem";
import ChangeHistoryPagination from "./components/ChangeHistoryPagination";
import { useChangeHistoryData } from "./hooks/useChangeHistoryData";
import { useChangeHistoryFormatter } from "./hooks/useChangeHistoryFormatter";

interface ChangeHistoryProps {
  recipeId?: string;
  readOnly?: boolean;
}

const ChangeHistory: React.FC<ChangeHistoryProps> = ({ recipeId, readOnly = false }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const { pageNumber, setPageNumber, historyItems, totalPages, isLoading, isError } =
    useChangeHistoryData(recipeId, !isCollapsed);
  const { formatDate, getOperationLabel } = useChangeHistoryFormatter();

  if (!recipeId) {
    return null;
  }

  return (
    <div className="tw-mt-4 tw-mb-6">
      <div className="tw-rounded-lg tw-border tw-border-border tw-bg-background tw-w-full sm:tw-max-w-8xl tw-mx-auto">
        {/* Header */}
        <ChangeHistoryHeader
          isCollapsed={isCollapsed}
          onToggle={() => setIsCollapsed(!isCollapsed)}
          itemCount={historyItems.length}
        />

        {/* Content */}
        {!isCollapsed && (
          <div className="tw-px-6 tw-py-6">
            {isLoading ? (
              <div className="tw-flex tw-items-center tw-justify-center tw-py-8">
                <Loader className="tw-h-5 tw-w-5 tw-animate-spin tw-text-primary tw-mr-2" />
                <span className="tw-text-sm tw-text-foreground/60">Loading history...</span>
              </div>
            ) : isError ? (
              <div className="tw-p-4 tw-bg-destructive/10 tw-border tw-border-destructive/20 tw-rounded-lg">
                <p className="tw-text-sm tw-text-destructive">Failed to load change history</p>
              </div>
            ) : historyItems.length === 0 ? (
              <div className="tw-p-4 tw-bg-foreground/5 tw-border tw-border-border tw-rounded-lg">
                <p className="tw-text-sm tw-text-foreground/60">No change history available</p>
              </div>
            ) : (
              <div className="tw-space-y-4">
                {historyItems.map((item, idx) => (
                  <ChangeHistoryItemComponent
                    key={item.id || idx}
                    item={item}
                    formatDate={formatDate}
                    getOperationLabel={getOperationLabel}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Pagination */}
        <ChangeHistoryPagination
          pageNumber={pageNumber}
          totalPages={totalPages}
          onPageChange={setPageNumber}
          isVisible={!isCollapsed && historyItems.length > 0}
        />
      </div>
    </div>
  );
};

export default ChangeHistory;
